// English translations
const en = {
  navigation: {
    home: 'Home',
    services: 'Services',
    'success-stories': 'Success Stories',
    products: 'Products',
    contact: 'Contact',
  },

  hero: {
    title: 'Where global engineering meets local trust',
    subtitle:
      'We build custom technology solutions to drive your business forward with innovation and quality.',
    cta: "Let's talk about your project",
  },

  services: {
    sectionTitle: 'Our Services',
    sectionSubtitle:
      'End-to-end solutions to accelerate your digital transformation.',
    items: {
      automatizaciones: {
        title: 'Automation',
        description:
          'Automated workflows, smart forms, and custom systems to optimize your key business processes.',
      },
      'mobile-apps': {
        title: 'Mobile Apps',
        description:
          'Native iOS and Android apps, PWAs, and functional prototypes fully integrated with your systems.',
      },
      integraciones: {
        title: 'Integrations',
        description:
          'REST & GraphQL APIs, ERP/CRM/CMS connectors, and real-time data synchronization.',
      },
      outsourcing: {
        title: 'Resource Outsourcing',
        description:
          'Flexible specialized teams and profiles: frontend, backend, QA, DevOps, and more.',
      },
      'desarrollo-web': {
        title: 'Web Development',
        description:
          'Modern websites, custom platforms, and interactive microsites built with the latest technologies.',
      },
      'bases-de-datos': {
        title: 'Databases',
        description:
          'Design, migration, and optimization of relational and NoSQL databases for high performance.',
      },
      cloud: {
        title: 'Cloud & DevOps',
        description:
          'Cloud infrastructure, CI/CD, containers, and automated deployments on AWS, Azure, and GCP.',
      },
      seguridad: {
        title: 'Security',
        description:
          'Security audits, best practices implementation, and sensitive data protection.',
      },
      analitica: {
        title: 'Analytics & BI',
        description:
          'Interactive dashboards, automated reports, and data visualization for smarter decisions.',
      },
    },
  },

  stats: {
    motto:
      'A Software Factory that builds innovative technology solutions. Our team is made up of passionate experts.',
    items: {
      years: 'Years of Experience',
      clients: 'Satisfied Clients',
      projects: 'Projects Delivered',
      experts: 'Experts on the Team',
    },
  },

  successStories: {
    sectionTitle: 'Success Stories',
    sectionSubtitle:
      'Discover how we helped our clients achieve their goals.',
    readMore: 'Read more',
    prevButton: 'Previous',
    nextButton: 'Next',
    items: {
      'story-1': {
        title: 'Retail Digital Transformation',
        description:
          'Full e-commerce platform migration with real-time inventory integration.',
        category: 'E-commerce',
        imageAlt: 'E-commerce platform with inventory integration',
      },
      'story-2': {
        title: 'Logistics Management App',
        description:
          'Mobile app for shipment tracking and delivery route optimization.',
        category: 'Logistics',
        imageAlt: 'Mobile logistics tracking app',
      },
      'story-3': {
        title: 'Online Booking System',
        description:
          'Booking platform with real-time availability and integrated payments.',
        category: 'Hospitality',
        imageAlt: 'Online booking system for hospitality',
      },
      'story-4': {
        title: 'Analytics Dashboard',
        description:
          'Control panel with real-time metrics and automated reports for data-driven decisions.',
        category: 'Business Intelligence',
        imageAlt: 'Analytics dashboard with real-time metrics',
      },
      'story-5': {
        title: 'Process Automation',
        description:
          'Automated workflows that reduced operational times by 60% for a financial company.',
        category: 'Fintech',
        imageAlt: 'Process automation for a financial company',
      },
      'story-6': {
        title: 'Educational Platform',
        description:
          'LMS with integrated video conferencing, assessments, and student progress tracking.',
        category: 'Education',
        imageAlt: 'Educational platform with integrated video conferencing',
      },
    },
  },

  partners: {
    sectionTitle: 'Technologies we work with',
    sectionSubtitle:
      'We leverage leading technologies and platforms to build custom, scalable, and reliable solutions.',
  },

  products: {
    overview: {
      eyebrow: 'DSW Products',
      title: 'Software built for your industry',
      subtitle:
        'Ready-to-use products built and supported by our engineering team.',
      ctaTrial: 'Try free for 30 days',
      ctaLearnMore: 'Learn more',
      benefitsTitle: 'Why choose DSW products?',
      benefitsSubtitle:
        "We don't just build custom software. We build products that solve real problems.",
      benefits: {
        support: {
          title: 'Direct support',
          description:
            'You work with the same team that builds the product. No middlemen, no lost tickets.',
        },
        scalable: {
          title: 'Scalable',
          description:
            'Designed to grow with your business. From a single office or store to multiple locations.',
        },
        secure: {
          title: 'Secure and reliable',
          description:
            'Encrypted data, automatic backups, and compliance with local privacy regulations.',
        },
        custom: {
          title: 'Adaptable',
          description:
            "If you need something specific for your business, we'll build it. You have direct access to the team behind it.",
        },
      },
    },
    medicare: {
      meta: {
        title: 'Medicare — All-in-one platform for medical practices and clinics',
      },
      hero: {
        eyebrow: 'Healthcare',
        title: 'Medicare',
        tagline:
          'The all-in-one platform for medical practices, clinics, and healthcare centers.',
        description:
          'Manage appointments, medical records, digital prescriptions, and patients from a single place. Designed by healthcare professionals, built with modern technology.',
        ctaTrial: 'Try free for 30 days',
        ctaContact: 'Talk to sales',
      },
      teaser: {
        category: 'Healthcare',
        title: 'Medicare',
        subtitle: 'All-in-one platform for medical practices and clinics',
        description:
          'Online appointments, digital medical records, and electronic prescriptions in one platform.',
      },
      features: {
        appointments: {
          title: 'Online appointments',
          description:
            'Patients book appointments 24/7 from their phone. Automatic email and WhatsApp reminders.',
        },
        records: {
          title: 'Digital medical records',
          description:
            'All patient medical information accessible instantly, secure and centralized.',
        },
        prescriptions: {
          title: 'Digital prescriptions',
          description:
            'Generate digitally signed electronic prescriptions, ready to send to the patient or pharmacy.',
        },
        monitoring: {
          title: 'Patient monitoring',
          description:
            'Track treatments, progress, and upcoming check-ups. Automatic alerts so you never miss follow-ups.',
        },
        multiUser: {
          title: 'Multi-professional',
          description:
            'Each provider has their own schedule and history. Frictionless coordination across specialties.',
        },
        security: {
          title: 'Security and privacy',
          description:
            'End-to-end encryption, automatic backups, and compliance with medical data regulations.',
        },
      },
      audience: {
        title: 'Who is Medicare for?',
        items: [
          'Private medical practices',
          'Small and medium-sized clinics',
          'Diagnostic centers',
          'Independent practitioners (physiotherapists, psychologists, nutritionists)',
          'Dental practices',
        ],
      },
      finalCta: {
        title: 'Get started with Medicare today',
        subtitle: 'Try it free for 30 days, no credit card required.',
        ctaTrial: 'Try free for 30 days',
      },
    },
    tienda360: {
      meta: {
        title: 'Tienda360 — Inventory and sales management for retail',
      },
      hero: {
        eyebrow: 'Retail',
        title: 'Tienda360',
        tagline:
          'Inventory, sales, and reports in one platform. For retailers who want to grow without losing control.',
        description:
          'Forget spreadsheets. Tienda360 gives you full visibility of your business: what sells, what runs low, what brings the best margins.',
        ctaTrial: 'Try free for 30 days',
        ctaContact: 'Talk to sales',
      },
      teaser: {
        category: 'Retail',
        title: 'Tienda360',
        subtitle: 'Inventory and sales management for retail',
        description:
          'Real-time inventory, point of sale, and business reports in one intuitive platform.',
      },
      features: {
        inventory: {
          title: 'Real-time inventory',
          description:
            'Know instantly what you have, what you need, and what moves the most. Automatic low-stock alerts.',
        },
        pos: {
          title: 'Point of sale',
          description:
            'Check out fast with a POS designed for real retail. Works on tablet, computer, or mobile.',
        },
        reports: {
          title: 'Business reports',
          description:
            'Best sellers, margins by category, performance by branch. Decisions backed by data, not guesswork.',
        },
        billing: {
          title: 'Electronic invoicing',
          description:
            'Issue legal electronic invoices in seconds. Ready-to-use AFIP/SAT integration.',
        },
        multiBranch: {
          title: 'Multi-branch',
          description:
            'Manage all your branches from a single dashboard. Centralized inventory, consolidated reports.',
        },
        payments: {
          title: 'Multiple payment methods',
          description:
            'Cash, card, QR, transfer. All automatically reconciled in your end-of-day report.',
        },
      },
      audience: {
        title: 'Who is Tienda360 for?',
        items: [
          'Retail stores (clothing, bookshops, hardware, etc.)',
          'Multi-branch retail chains',
          'Neighborhood grocery stores and convenience shops',
          'Specialty product stores',
          'Growing businesses',
        ],
      },
      finalCta: {
        title: 'Get started with Tienda360 today',
        subtitle: 'Try it free for 30 days, no credit card required.',
        ctaTrial: 'Try free for 30 days',
      },
    },
  },

  contact: {
    title: 'Contact Us',
    description:
      "We're here to help. Fill out the form and we'll get back to you as soon as possible.",
    labels: {
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email',
      message: 'Message',
    },
    submitButton: 'Send Message',
    submitting: 'Sending...',
    validation: {
      required: 'This field is required',
      invalidEmail: 'Invalid email address',
    },
    feedback: {
      success: 'Message sent! We will get back to you shortly.',
      errorGeneric: 'Failed to send message. Please try again later.',
      errorInvalid: 'Invalid data. Please check your inputs.',
      errorConnection: 'Connection error. Please check your internet and try again.',
    },
  },

  footer: {
    tagline: 'Innovative technology solutions to power your business.',
    copyright: '© 2024 DSW Solutions. All rights reserved.',
    sections: {
      empresa: {
        title: 'Company',
        links: {
          inicio: 'Home',
          servicios: 'Services',
          casosDeExito: 'Success Stories',
          productos: 'Products',
        },
      },
      productos: {
        title: 'Products',
        links: {
          medicare: 'Medicare',
          tienda360: 'Tienda360',
          allProducts: 'View all',
        },
      },
      legal: {
        title: 'Legal',
        links: {
          privacidad: 'Privacy Policy',
          avisoLegal: 'Legal Notice',
          sitemap: 'Sitemap',
        },
      },
    },
  },
};

export default en;
